import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // ✅ Init submenu logic
    this.#initChildMenus();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);

    // ✅ Clean observer
    this.#observer?.disconnect();
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#close(this.#getDetailsElement(event));
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

      onAnimationEnd(drawer || details, () => {
        trapFocus(details);
      }, { subtree: false });
    });
  }

  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);

        if (details === this.refs.details) {
          removeTrapFocus();

          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Mobile submenu: title + chevron both toggle (no parent link navigation)
   */
  #initChildMenus = () => {
    const setupMenus = () => {
      const childMenus = this.querySelectorAll('.menu-drawer__menu--childlist');

      childMenus.forEach((menu) => {
        const parentItem = menu.closest('li');
        if (!parentItem || parentItem.dataset.processed === 'true') return;

        // Parent title row (sibling of the childlist), not a child link
        let trigger = null;
        for (const child of parentItem.children) {
          if (child.matches('a.menu-drawer__menu-item, button.menu-drawer__menu-item, .menu-drawer__menu-item')) {
            trigger = child;
            break;
          }
          if (child.matches('accordion-custom')) {
            trigger = child.querySelector('summary.menu-drawer__menu-item');
            break;
          }
        }

        if (!trigger) return;
        parentItem.dataset.processed = 'true';

        menu.style.height = '0px';
        menu.style.overflow = 'hidden';
        menu.style.transition = 'height 0.3s ease';
        parentItem.classList.add('is-collapsed');

        const icon = document.createElement('span');
        icon.className = 'menu-chevron';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML =
          '<svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg" class="chevron-right"><path d="M1.5 11L6.5 6L1.5 1" stroke="#6DBDB2" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
        icon.style.pointerEvents = 'none';
        trigger.appendChild(icon);

        // Parent with children should not navigate — title opens submenu instead
        if (trigger instanceof HTMLAnchorElement) {
          trigger.setAttribute('role', 'button');
          trigger.setAttribute('aria-expanded', 'false');
          trigger.style.cursor = 'pointer';
        }

        const setOpen = (open) => {
          if (open) {
            menu.style.height = 'auto';
            const fullHeight = menu.scrollHeight;
            menu.style.height = '0px';
            void menu.offsetHeight;
            menu.style.height = `${fullHeight}px`;
            parentItem.classList.add('is-open');
            parentItem.classList.remove('is-collapsed');
            trigger.setAttribute('aria-expanded', 'true');
          } else {
            menu.style.height = `${menu.scrollHeight}px`;
            void menu.offsetHeight;
            menu.style.height = '0px';
            parentItem.classList.remove('is-open');
            parentItem.classList.add('is-collapsed');
            trigger.setAttribute('aria-expanded', 'false');
          }
        };

        // One listener only (chevron has pointer-events:none so title + arrow share this)
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          setOpen(!parentItem.classList.contains('is-open'));
        });
      });
    };

    setupMenus();

    this.#observer = new MutationObserver(() => {
      setupMenus();
    });

    this.#observer.observe(this, {
      childList: true,
      subtree: true,
    });
  };

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}